import {
  type KycResponse,
  type IdentitiesKycStatus,
  KycCurrentStatus,
  KycProcessType,
  KycProcessStatus,
} from '@repo/api-kit/queries/due-diligence/kyc';

interface IdentitiesKycLike {
  kycStatus: IdentitiesKycStatus;
  isReKycRequired: boolean;
  isRenewalPoiRequired?: boolean;
  reKycDueDate?: string | Date | null;
}

const KYC_STATUS_TO_CURRENT_STATUS: Partial<
  Record<IdentitiesKycStatus, KycResponse['kycCurrentStatus']>
> = {
  accepted: KycCurrentStatus.accepted,
  pending: KycCurrentStatus.pending,
  refused: KycCurrentStatus.refused,
  waiting_document: KycCurrentStatus.pending,
};

function resolveKycCurrentStatus(
  identitiesKyc: IdentitiesKycLike
): KycResponse['kycCurrentStatus'] {
  // In the old API, a past-due re-kyc user (restricted/suspended) has a non-accepted kycStatus (e.g. 'waiting_document').
  // The new API returns `pending` in this case, so we normalize it here.
  // A user who still has time has `accepted` kycStatus and we keep it as-is.
  if (identitiesKyc.isReKycRequired && identitiesKyc.kycStatus !== 'accepted') {
    return KycCurrentStatus.pending;
  }
  return (
    KYC_STATUS_TO_CURRENT_STATUS[identitiesKyc.kycStatus] ??
    KycCurrentStatus.KYC_CURRENT_STATUS_UNSPECIFIED
  );
}

function resolveProcessStatus(identitiesKyc: IdentitiesKycLike) {
  // In the old API, `waiting_document` means the user's account is restricted and they need to take action.
  // This maps to `pending_user_action` in the new API regardless of process type.
  if (identitiesKyc.kycStatus === 'waiting_document') {
    return KycProcessStatus.pending_user_action;
  }
  return KycProcessStatus.pending_liveness;
}

function resolveActiveProcess(identitiesKyc: IdentitiesKycLike): KycResponse['activeProcess'] {
  const baseProcess = {
    provider: null,
    providerDetails: null,
    signatureRequired: false,
  } as const;

  if (identitiesKyc.isReKycRequired) {
    return {
      ...baseProcess,
      type: KycProcessType.re_kyc,
      processStatus: resolveProcessStatus(identitiesKyc),
      dueDate: identitiesKyc.reKycDueDate ? new Date(identitiesKyc.reKycDueDate) : null,
    };
  }

  if (identitiesKyc.isRenewalPoiRequired) {
    return {
      ...baseProcess,
      type: KycProcessType.poi_renewal,
      processStatus: resolveProcessStatus(identitiesKyc),
      dueDate: null,
    };
  }

  // In the old API, `waiting_document` means the user submitted KYC but needs to take action again.
  // In the new API, this maps to an initial process with `pending_user_action` status.
  if (identitiesKyc.kycStatus === 'waiting_document') {
    return {
      ...baseProcess,
      type: KycProcessType.initial,
      processStatus: KycProcessStatus.pending_user_action,
      dueDate: null,
    };
  }

  return null;
}

export function mapIdentitiesToKycResponse(identitiesKyc: IdentitiesKycLike): KycResponse;
export function mapIdentitiesToKycResponse(
  identitiesKyc?: IdentitiesKycLike | null
): KycResponse | undefined {
  if (!identitiesKyc) return undefined;

  return {
    kycCurrentStatus: resolveKycCurrentStatus(identitiesKyc),
    activeProcess: resolveActiveProcess(identitiesKyc),
  };
}
